/**
 * 問題のインデックス（一覧の並び、手法別・レッスン別・ユニット別・文法別）。ブラウザで使う（remark を含まない）。
 * 一覧はホームの入口（problem-format.md §7。order 順、draft を除く）。
 */
import type {
  ExerciseMeta,
  ProblemCase,
  ProblemDoc,
  ProblemMeta,
  Syntax,
  Technique,
} from './types.js';
import { PROBLEM_DRAFT_KEY_PATTERN } from './types.js';

export interface ProblemIndex {
  /** 一覧に出す問題（draft を除く）。order 昇順。 */
  problems: ProblemDoc[];
  /** draft も含む（id で開けるようにする）。 */
  byId: ReadonlyMap<string, ProblemDoc>;
  /** 手法 → 問題 id（例題の techniques。draft を除く。order 順）。 */
  byTechnique: ReadonlyMap<Technique, string[]>;
  /** 手法 → 問題 id（例題と類題の techniques のどれかに含むもの）。 */
  byAnyTechnique: ReadonlyMap<Technique, string[]>;
  /** 文法 → 問題 id。 */
  bySyntax: ReadonlyMap<Syntax, string[]>;
  /** レッスン id → そのレッスンに立ち返る問題 id。 */
  byLesson: ReadonlyMap<string, string[]>;
  /** ユニット（レッスン id の `<unit>-<NN>-` の unit）→ 問題 id。 */
  byUnit: ReadonlyMap<string, string[]>;
}

function add<K>(map: Map<K, string[]>, key: K, id: string): void {
  const list = map.get(key) ?? [];
  if (!list.includes(id)) list.push(id);
  map.set(key, list);
}

/** レッスン id の unit（lesson-format.md §2.1）。形が違えば null。 */
export function lessonUnitOf(lessonId: string): string | null {
  return /^(.+)-[0-9]{2}-[a-z0-9]+(?:-[a-z0-9]+)*$/.exec(lessonId)?.[1] ?? null;
}

export function buildProblemIndex(docs: readonly ProblemDoc[]): ProblemIndex {
  const sorted = [...docs].sort(
    (a, b) =>
      a.meta.order - b.meta.order || (a.meta.id < b.meta.id ? -1 : a.meta.id > b.meta.id ? 1 : 0),
  );
  const byId = new Map<string, ProblemDoc>();
  for (const d of sorted) if (!byId.has(d.meta.id)) byId.set(d.meta.id, d);
  const problems = [...byId.values()].filter((d) => d.meta.draft !== true);
  const byTechnique = new Map<Technique, string[]>();
  const byAnyTechnique = new Map<Technique, string[]>();
  const bySyntax = new Map<Syntax, string[]>();
  const byLesson = new Map<string, string[]>();
  const byUnit = new Map<string, string[]>();
  for (const d of problems) {
    const { id } = d.meta;
    for (const t of d.meta.techniques) {
      add(byTechnique, t, id);
      add(byAnyTechnique, t, id);
    }
    for (const ex of d.meta.exercises)
      for (const t of ex.techniques ?? []) add(byAnyTechnique, t, id);
    for (const s of d.meta.syntax) add(bySyntax, s, id);
    for (const l of d.meta.lessons) {
      add(byLesson, l.id, id);
      const unit = lessonUnitOf(l.id);
      if (unit !== null) add(byUnit, unit, id);
    }
  }
  return { problems, byId, byTechnique, byAnyTechnique, bySyntax, byLesson, byUnit };
}

/** 書きかけのキー（§8）。例題は `<problemId>`、類題は `<problemId>/<exerciseId>`。 */
export function problemDraftKey(problemId: string, exerciseId?: string): string {
  return exerciseId === undefined ? problemId : `${problemId}/${exerciseId}`;
}

export function isProblemDraftKey(key: string): boolean {
  return PROBLEM_DRAFT_KEY_PATTERN.test(key);
}

/** 類題の手法（省略時は例題のもの）。 */
export function exerciseTechniques(problem: ProblemMeta, ex: ExerciseMeta): Technique[] {
  return ex.techniques ?? problem.techniques;
}

/** 類題の制限時間（省略時は例題のもの）。 */
export function exerciseTimeLimit(problem: ProblemMeta, ex: ExerciseMeta): number {
  return ex.time_limit_sec ?? problem.time_limit_sec;
}

/** 入出力例として出すケース（sample: true。例題は 1〜2 件。類題で 0 件なら 1 件目）。 */
export function sampleCases(cases: readonly ProblemCase[]): ProblemCase[] {
  const s = cases.filter((c) => c.sample === true);
  if (s.length > 0) return s;
  const first = cases[0];
  return first ? [first] : [];
}

/** 見出しの出典表記（「ABC000 A」）。 */
export function sourceLabel(source: { contest: string; problem: string }): string {
  return `${source.contest.toUpperCase()} ${source.problem}`;
}
